import { Button, Flex, Layout, Popconfirm, Typography, message } from 'antd'
import { Link } from 'react-router-dom'
import backButtonIcon from '../assets/ui_revamp/Back Button.svg'
import saveIcon from '../assets/ui_revamp/Save.svg'
import { CloseOutlined, KeyOutlined, RightOutlined, StarFilled, SyncOutlined } from '@ant-design/icons'
import Logo from '../assets/ui_revamp/logo.svg'
import ChevronDownIcon from '../assets/ui_revamp/Chevron Down.svg'
import ChevronUpIcon from '../assets/ui_revamp/Chevron Up.svg'
import exportIcon from '../assets/export.svg'
import IrradianceIcon from "../assets/ui_revamp/Irradiance.svg";
import SunVisualizationIcon from "../assets/ui_revamp/SunVisualization.svg";
import LidarIcon from "../assets/ui_revamp/Lidar.svg";
import ShowAllMeasurementsIcon from "../assets/ui_revamp/ShowAllMeasurements.svg";
import ToggleRulerIcon from "../assets/ui_revamp/ToggleRuler.svg";
import dropdownDotsIcon from "../assets/ui_revamp/dropdown_dots.svg";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { InputNumber, Select } from "antd";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { DrawingModes, POLYGONS, onModelLoaded } from "../ThreeJs/Global/variables";
import { useParams } from "react-router-dom";
import {
  fetchProduction,
  getLidar,
  markProjectComplete,
  markProjectIncomplete,
} from "../helpers/API";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { generatePvwattsReport } from "../helpers/GeneratePVWattsReport";
import { toast } from "../lib/toast";
import { notifyApiError } from "../helpers/errorNotify";

import { lidarLoaderState, magicToggleState, polygonsState, selectedIndicesState } from "../ThreeJs/ReactStates/states";
import { selectSegment, showSelectedSegmentLabel, toggleSegmentLabels } from "../ThreeJs/Segments/Functions";
import { sceneManager } from "../ThreeJs/SetUps/SceneManager";
import { initSunSimulation } from "../ThreeJs/Shading/sunPositioning";
import {
  addASAtoScene,
  disposeIrradianceOverlay,
  isIrradianceOverlay,
} from "../ThreeJs/Shading/ASADisplay";
import { DEV_MOD, LAT, LNG } from "../helpers/CONSTANT";
import { buildLidarMesh } from "../ThreeJs/Lidar/LidarMeshUtils.js";
import * as THREE from "three";
import { OrbitControls } from "../ThreeJs/Utils/OrbitControls";
import {
  DEFAULT_CAMERA_KEY_BINDINGS,
  applyCameraKeyBindings,
  bindingFromAnyKeyEvent,
  bindingFromMouseEvent,
  formatCameraBinding,
  loadCameraKeyBindings,
  saveCameraKeyBindings,
} from "../ThreeJs/Controls/CameraKeyBindings";
import { ensurePrecomputeForAllSegments, enqueueTofGtiIfNeeded } from "../helpers/IrradianceJobs";
import { IRR_CACHE, getOrCreateCache, invalidateIrradianceForSegment, markIrradianceClean } from "../helpers/IrradianceCache";
import { deleteAllSegments } from "../ThreeJs/Segments/Delete";
import { getExclusionSphereConfigs } from "../ThreeJs/Trees/treeDrawingUtils";
import {
  buildIrradianceProgressNotice,
  collectIrradianceDisplayPoints,
  getSegmentsMissingIrradiance,
  hasComputedIrradiance,
} from "../helpers/irradianceStatus";

ChartJS.register(BarElement, CategoryScale, LinearScale, ChartTooltip, Legend);

// Make THREE & OrbitControls available to child windows
if (typeof window !== "undefined") {
  window.THREE = window.THREE || THREE;
  window.OrbitControls = window.OrbitControls || OrbitControls;
}
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const ICON_BUTTON_WRAP_STYLE = {
  width: 40,
  height: 40,
  borderRadius: 10,
  background: "rgba(255,255,255,0.05)",
  border: "0.6px solid rgba(255,255,255,0.2)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  flex: "0 0 auto",
  pointerEvents: "auto",
  padding: 0,
  appearance: "none",
};

const ButtonCard = forwardRef(
  ({ children, style, disabled = false, as = "button", ...rest }, ref) => {
    const Component = as;
    const isButton = Component === "button";
    const hasClick = typeof rest.onClick === "function";

    return (
      <Component
        ref={ref}
        {...(isButton ? { type: "button" } : null)}
        {...(isButton ? { disabled } : { "aria-disabled": disabled })}
        style={{
          ...ICON_BUTTON_WRAP_STYLE,
          ...style,
          ...(hasClick && !disabled ? { cursor: "pointer" } : null),
          ...(disabled
            ? {
                cursor: "not-allowed",
                opacity: 0.38,
                filter: "grayscale(1) saturate(0.2)",
              }
            : null),
        }}
        {...rest}
      >
        {children}
      </Component>
    );
  }
);
ButtonCard.displayName = "ButtonCard";

/* ---------------------- per-segment ASA helper ---------------------- */
function computeASAForSegment(polygonGTI, polygonResult, segmentId = null) {
  const displayPoints = [];
  const monthlyTotals = Array.from({ length: 12 }, () => ({
    shaded: 0,
    unshaded: 0,
    count: 0,
  }));

  const points = polygonResult?.pointResults || [];

  points.forEach((res, index) => {
    const rayResults = res?.rayResults || [];
    const rayLookup = new Map();

    rayResults.forEach((r) => {
      const { day, month, hour } = r.date;
      const key = `${month}-${day}-${hour}`;
      rayLookup.set(key, !r.blocked);
    });

    let totalIrradiance = 0;
    let totalIrradianceUnshaded = 0;

    (polygonGTI || []).forEach((gti) => {
      const dateObj = new Date(gti.datetime);
      const day = dateObj.getDate();
      const month = dateObj.getMonth() + 1;
      const hour = dateObj.getHours();
      const key = `${month}-${day}-${hour}`;

      if (rayLookup.has(key)) {
        const multiplier = rayLookup.get(key) ? 1 : 0;

        totalIrradiance += gti.gti * multiplier;
        totalIrradianceUnshaded += gti.gti;

        monthlyTotals[month - 1].shaded += gti.gti * multiplier;
        monthlyTotals[month - 1].unshaded += gti.gti;
        monthlyTotals[month - 1].count += 1;
      }
    });

    const asa =
      totalIrradianceUnshaded > 0
        ? totalIrradiance / totalIrradianceUnshaded
        : 0;

    displayPoints.push({
      pointIndex: index,
      coordinates: res.position, // Vector3
      totalIrradiance,
      totalIrradianceUnshaded,
      asa,
      segmentId,
    });
  });

  const avgASA =
    displayPoints.length > 0
      ? displayPoints.reduce((sum, p) => sum + (p.asa || 0), 0) /
        displayPoints.length
      : 0;

  const monthlyASA = monthlyTotals.map((m, i) => {
    const asa = m.unshaded > 0 ? m.shaded / m.unshaded : 0;
    return { month: i + 1, asa };
  });

  const irradiancePoints = displayPoints.map((p) => ({
    pointIndex: p.pointIndex,
    coordinates: {
      x: p.coordinates?.x ?? 0,
      y: p.coordinates?.y ?? 0,
      z: p.coordinates?.z ?? 0,
    },
    totalIrradiance: p.totalIrradiance,
    totalIrradianceUnshaded: p.totalIrradianceUnshaded,
    asa: p.asa,
  }));

  return { avgASA, monthlyASA, irradiancePoints, displayPoints };
}

/* ---------------------- worker pool runner ---------------------- */
/**
 * Runs GPU raycast jobs "fully parallel" using multiple workers.
 * NOTE: This parallelizes the slow server fetches. Payload building stays on main thread (needs Three.js scene).
 */
async function runGpuRaycastJobsInPool({
  jobs,
  endpointUrl,
  poolSize = 8,
  onJobComplete,
}) {
  const results = new Map();
  const errors = new Map();

  if (!jobs.length) return { results, errors };

  const workerCount = Math.max(1, Math.min(poolSize, jobs.length));
  const workers = [];
  const jobQueue = [...jobs];

  let globalJobId = 1;
  const inflight = new Map(); // jobId -> resolve

  const makeWorker = () =>
    new Worker(new URL("../helpers/gpuRaycastWorker.js", import.meta.url));

  for (let i = 0; i < workerCount; i++) {
    const w = makeWorker();

    w.onmessage = (e) => {
      const msg = e.data || {};
      const { jobId } = msg;

      const resolver = inflight.get(jobId);
      if (resolver) {
        inflight.delete(jobId);
        resolver(msg);
      }
    };

    w.onerror = (err) => {
      console.error("gpuRaycastWorker error:", err);
    };

    workers.push(w);
  }

  const runWorkerLoop = async (worker) => {
    while (jobQueue.length > 0) {
      const job = jobQueue.shift();
      if (!job) break;

      const jobId = globalJobId++;
      const payload = {
        jobId,
        segmentId: job.segmentId,
        endpointUrl,
        payload: job.payload,
      };

      const msg = await new Promise((resolve) => {
        inflight.set(jobId, resolve);
        worker.postMessage(payload);
      });

      if (typeof onJobComplete === "function") {
        onJobComplete(msg, job);
      }

      if (msg?.ok) {
        results.set(msg.segmentId, msg.hits);
      } else {
        errors.set(msg.segmentId, msg?.error || "Unknown GPU raycast error");
      }
    }
  };

  await Promise.all(workers.map((w) => runWorkerLoop(w)));
  workers.forEach((w) => w.terminate());

  return { results, errors };
}

const HIDDEN_LIDAR_LOADER = Object.freeze({
  visible: false,
  title: "",
  stageLabel: "",
  startedAt: null,
  progress: 0,
});

const ACTION_BUTTON_STYLE = {
  boxSizing: "border-box",
  height: 40,
  padding: "10px",
  borderRadius: 10,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 5,
  background: "rgba(255, 255, 255, 0.05)",
  border: "0.6px solid rgba(255, 255, 255, 0.2)",
  color: "#FFFFFF",
  fontFamily: '"Reddit Sans", sans-serif',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "20px",
  boxShadow: "none",
};

const ACTION_ICON_STYLE = { width: 18, height: 18, display: "block", flex: "0 0 auto" };

const estimateLidarProgress = (startedAt) => {
  if (!Number.isFinite(startedAt)) return 0;
  const elapsedSeconds = Math.max(0, Date.now() - startedAt) / 1000;
  return Math.round(95 * (1 - Math.exp(-elapsedSeconds / 240)));
};

// Must live outside AppHeader. Keeping this component stable prevents its icon
// DOM from being remounted every time PLY job progress arrives.
const MenuActionButton = ({
  icon,
  label,
  onClick,
  disabled = false,
  active = false,
  style,
  iconStyle,
  iconNode,
  title,
  className = "",
  children,
}) => (
  <Button
    type="default"
    className={`app-header-action-btn${active ? " is-active" : ""}${className ? ` ${className}` : ""}`}
    title={title}
    icon={
      iconNode || (icon ? (
        <img src={icon} alt="" aria-hidden="true" draggable={false} style={{ ...ACTION_ICON_STYLE, ...(iconStyle || {}) }} />
      ) : null)
    }
    disabled={disabled}
    onClick={onClick}
    style={{
      ...ACTION_BUTTON_STYLE,
      ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : { cursor: "pointer" }),
      ...(style || {}),
    }}
  >
    {children || <span style={{ color: "inherit", WebkitTextFillColor: "inherit" }}>{label}</span>}
  </Button>
);

const AppHeader = ({
  theme,
  currentProject,
  onDownloadShadeReport,
  onDownloadFacetReport,
  onGenerateDxf,
  onSave,
  saveDisabled,
  panelList = [],
  currentDesign,
  onDesignRefresh,
  updateDrawingMode,
  currentDrawingMode,
}) => {

    const { token, designID } = useParams();

  const latitude = currentDesign?.latitude ? parseFloat(currentDesign.latitude) : null;
  const longitude = currentDesign?.longitude ? parseFloat(currentDesign.longitude) : null;

  const toolLatitude = DEV_MOD ? parseFloat(LAT) : latitude;
  const toolLongitude = DEV_MOD ? parseFloat(LNG) : longitude;

  const [selectedIndices, setSelectedIndices] = useRecoilState(selectedIndicesState);
  const [magicToggle, setMagicToggle] = useRecoilState(magicToggleState);
  const setPolygons = useSetRecoilState(polygonsState);
  const setLidarLoader = useSetRecoilState(lidarLoaderState);
  const lidarLoader = useRecoilValue(lidarLoaderState);
  const [asaSprites, setAsaSprites] = useState([]);
  const [irradianceLoading, setIrradianceLoading] = useState(false);
  const [irradianceProgress, setIrradianceProgress] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [lidarData, setLidarData] = useState();
  const [lidarTitle, setLidarTitle] = useState("Lidar");
  const [isLidarBusy, setIsLidarBusy] = useState(false);
  const [isIrradianceActive, setIsIrradianceActive] = useState(false);
  const [isLidarActive, setIsLidarActive] = useState(false);
  const [isSunVisualizationActive, setIsSunVisualizationActive] = useState(false);
  const isLidarUnavailable = !lidarData && lidarTitle === "Lidar not available";

  const [segmentProductions, setSegmentProductions] = useState([]);
  const [segmentRequestParams, setSegmentRequestParams] = useState([]);
  const latestSegmentProductionsRef = useRef([]);
  const latestSegmentRequestParamsRef = useRef([]);
  const [totalLoss, setTotalLoss] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isGenerationOpen, setIsGenerationOpen] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [isGenerateSubmenuOpen, setIsGenerateSubmenuOpen] = useState(false);
  const [isKeyBindingsModalOpen, setIsKeyBindingsModalOpen] = useState(false);
  const [cameraKeyBindings, setCameraKeyBindings] = useState(() => loadCameraKeyBindings());
  const [draftCameraKeyBindings, setDraftCameraKeyBindings] = useState(() => loadCameraKeyBindings());
  const [capturingBinding, setCapturingBinding] = useState(null);
  const [keyBindingsError, setKeyBindingsError] = useState("");
  const conflictingBindingKeys = useMemo(() => {
    const keys = ["rotate", "pan", "panModifier", "zoomIn", "zoomOut"];
    return new Set(keys.filter((key) => keys.some((otherKey) => otherKey !== key && draftCameraKeyBindings[key] === draftCameraKeyBindings[otherKey])));
  }, [draftCameraKeyBindings]);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [isStatusActionLoading, setIsStatusActionLoading] = useState(false);
  const [localDesignStatus, setLocalDesignStatus] = useState(
    currentDesign?.status || currentProject?.status || null
  );
  const [polygonsTick, setPolygonsTick] = useState(0);
  const actionMenuRef = useRef(null);
  const [surveyRatings, setSurveyRatings] = useState({
    cameraTechnique: 0,
    flightPath: 0,
    imageQuality: 0,
  });

  const [systemDetails, setSystemDetails] = useState({
    module_type: 0,
    dc_ac_ratio: 1.5,
    inv_eff: 97.0,
    dataset: "nsrdb",
  });

  const [values, setValues] = useState({
    moduleNameplateRating: 1,
    mismatch: 1.5,
    connections: 0.5,
    lightInducedDegradation: 1.5,
    wiring: 2,
    soiling: 2,
    soilingPeriod: "Annual",
    availability: 1,
    age: 0,
    snow: 4,
    snowPeriod: "Annual",
    other: 0,
  });

  const calculateTotalLoss = () => {
    const total =
      values.moduleNameplateRating +
      values.mismatch +
      values.connections +
      values.lightInducedDegradation +
      values.wiring +
      values.soiling +
      values.availability +
      values.age +
      values.snow +
      values.other;

    return total > 100 ? 100 : parseFloat(total.toFixed(2));
  };

  useEffect(() => {
    setTotalLoss(calculateTotalLoss());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const buildSegmentParams = () => {
    const dynamicLoss = calculateTotalLoss();

    let panelPmax = 0;
    const samplePanel = POLYGONS.flatMap((seg) => seg.panels || [])[0];
    if (samplePanel) {
      const matchedPanel = panelList.find((p) => p.id === samplePanel.id);
      if (matchedPanel) panelPmax = parseInt(matchedPanel.pmax, 10) / 1000;
    }

    return POLYGONS.filter((seg) => seg.panels?.length > 0).map((segment) => {
      const irr = segment?.irradiance;
      const asa = irr?.ASA;
      const hasIrradianceData =
        (Array.isArray(irr?.monthlyASA) && irr.monthlyASA.length > 0) ||
        (Array.isArray(irr?.irradiancePoints) && irr.irradiancePoints.length > 0);
      const hasValidASA = typeof asa === "number" && Number.isFinite(asa);
      const irradianceLoss = hasIrradianceData && hasValidASA
        ? Math.max(0, Math.min(100, (1 - asa) * 100))
        : 0;
      const segmentLosses = Math.min(100, dynamicLoss + irradianceLoss);

      return {
      lat: latitude,
      lon: longitude,
      tilt: segment.details?.tilt || 33,
      azimuth: segment.details?.azimuth || 180,
      capacity: (segment.panels?.length || 0) * panelPmax,
      module_type: systemDetails.module_type,
      array_type: 0,
      losses: parseFloat(segmentLosses.toFixed(2)),
      soiling:
        values.soilingPeriod === "Monthly"
          ? "2|2|3|4|5|5|6|5|4|3|3|2"
          : 3,
      albedo: 0.2,
      bifaciality: 0,
      gcr: 0.4,
      dc_ac_ratio: systemDetails.dc_ac_ratio,
      inv_eff: systemDetails.inv_eff,
      dataset: systemDetails.dataset,
    };
    });
  };

  const updateIrradianceProgressState = ({
    totalSegments,
    completedSegments = 0,
    followUpLabel = "",
  }) => {
    const nextNotice = buildIrradianceProgressNotice({
      totalSegments,
      completedSegments,
      followUpLabel,
    });

    setIrradianceProgress(nextNotice);
  };

  const clearIrradianceProgressState = () => {
    setIrradianceProgress(null);
  };

  const getIrradianceOverlayVisible = () => {
    const scene = sceneManager.scene;
    if (!scene) return false;

    let visible = false;
    scene.traverse((obj) => {
      if (visible) return;
      if (isIrradianceOverlay(obj) && obj.visible !== false) {
        visible = true;
      }
    });
    return visible;
  };

  const getLidarVisible = () => {
    const scene = sceneManager.scene;
    if (!scene) return false;

    let visible = false;
    scene.traverse((obj) => {
      if (visible) return;
      if (obj?.name === "lidarTerrainMesh" && obj.visible) {
        visible = true;
      }
    });
    return visible;
  };

  const getSunVisualizationVisible = () => {
    const scene = sceneManager.scene;
    if (!scene) return false;

    let visible = false;
    scene.traverse((obj) => {
      if (visible) return;
      if (obj?.userData?.isSunPath) {
        visible = true;
      }
    });
    return visible;
  };

  const showIrradianceOverlay = () => {
    const scene = sceneManager.scene;
    if (!scene) return false;

    const existingSprites = [];
    scene.traverse((obj) => {
      if (isIrradianceOverlay(obj)) existingSprites.push(obj);
    });

    if (existingSprites.length > 0) {
      const currentlyVisible = existingSprites.some((sprite) => sprite.visible !== false);
      existingSprites.forEach((sprite) => {
        sprite.visible = !currentlyVisible;
      });
      setAsaSprites(existingSprites);
      setIsIrradianceActive(!currentlyVisible);
      return true;
    }

    const allDisplayPts = collectIrradianceDisplayPoints(POLYGONS);
    if (allDisplayPts.length === 0) {
      return false;
    }

    const newSprites = addASAtoScene(sceneManager.scene, allDisplayPts);
    setAsaSprites(newSprites);
    setIsIrradianceActive(newSprites.length > 0);
    return true;
  };

  const fetchProductionForCurrentConfig = async () => {
    if (!token || !POLYGONS?.length || !panelList?.length || latitude == null || longitude == null) {
      return { data: [], segmentParams: [] };
    }

    const segmentParams = buildSegmentParams();
    const res = await fetchProduction(segmentParams);
    return {
      data: Array.isArray(res?.data) ? res.data : [],
      segmentParams,
    };
  };

  const simulateSystem = async () => {
    setLoading(true);
    try {
      const { data, segmentParams } = await fetchProductionForCurrentConfig();
      latestSegmentProductionsRef.current = data;
      latestSegmentRequestParamsRef.current = segmentParams || [];
      setSegmentProductions(data);
      setSegmentRequestParams(segmentParams || []);
      return data;
    } catch (err) {
      notifyApiError(err, {
        fallback:
          "Couldn't update production estimates. The production service may be temporarily unavailable.",
        key: "production-sim",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSimulateSystem = async () => {
    if (loading || reportLoading || irradianceLoading) return;
    const ready = await ensureIrradianceReady({
      followUpLabel: "Simulate System",
    });
    if (!ready) return;
    await simulateSystem();
  };

  const hasAutoSimulatedRef = useRef(false);

  useEffect(() => {
    hasAutoSimulatedRef.current = false;
  }, [token]);

  useEffect(() => {
    if (hasAutoSimulatedRef.current) return;
    if (!token || !POLYGONS?.length || !panelList?.length || latitude == null || longitude == null) return;

    hasAutoSimulatedRef.current = true;
    simulateSystem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, panelList, latitude, longitude, polygonsTick]);

  const handleValueChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value ?? 0 }));
  };

  const handleSystemDetailChange = (field, value) => {
    setSystemDetails((prev) => ({ ...prev, [field]: value }));
  };

  const projectId =
    currentDesign?.id ||
    currentDesign?.project_id ||
    currentProject?.id ||
    currentProject?.project_id ||
    null;
  const designStatus = localDesignStatus;
  const canMarkIncomplete =
    designStatus?.id === 3 && designStatus?.slug === "complete" && !!projectId;
  const canMarkComplete =
    designStatus?.id === 10 && designStatus?.slug === "model-generated" && !!projectId;
  const overallSurveyRating =
    (
      (surveyRatings.cameraTechnique || 0) +
      (surveyRatings.flightPath || 0) +
      (surveyRatings.imageQuality || 0)
    ) / 3;
  const hasAllSurveyRatings = Object.values(surveyRatings).every(
    (value) => Number(value) > 0
  );

  const resetSurveyRatings = () => {
    setSurveyRatings({
      cameraTechnique: 0,
      flightPath: 0,
      imageQuality: 0,
    });
  };

  const closeRatingModal = () => {
    setIsRatingModalOpen(false);
    resetSurveyRatings();
  };

  useEffect(() => {
    setLocalDesignStatus(currentDesign?.status || currentProject?.status || null);
  }, [currentDesign?.status, currentProject?.status]);

  const handleMarkIncomplete = async () => {
    if (!token || !projectId || isStatusActionLoading) return;

    setIsStatusActionLoading(true);
    try {
      await markProjectIncomplete(token, projectId);
      setLocalDesignStatus({
        ...(designStatus || {}),
        id: 10,
        slug: "model-generated",
      });
      toast.success("Survey marked as incomplete.");
      setIsActionMenuOpen(false);
      setIsGenerateSubmenuOpen(false);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to mark survey as incomplete.";
      toast.error(errorMessage);
    } finally {
      setIsStatusActionLoading(false);
    }
  };

  const handleOpenMarkCompleteModal = () => {
    if (!canMarkComplete || isStatusActionLoading) return;
    resetSurveyRatings();
    setIsRatingModalOpen(true);
    setIsActionMenuOpen(false);
    setIsGenerateSubmenuOpen(false);
  };

  const handleSubmitSurveyRating = async () => {
    if (!token || !projectId || !hasAllSurveyRatings || isStatusActionLoading) return;

    setIsStatusActionLoading(true);
    try {
      if (typeof onSave === "function") {
        await Promise.resolve(onSave());
      }

      const reportPrereqs = await collectReportPrereqs();

      await markProjectComplete(
        token,
        projectId,
        Number(overallSurveyRating.toFixed(2))
      );
      setLocalDesignStatus({
        ...(designStatus || {}),
        id: 3,
        slug: "complete",
      });
      closeRatingModal();
      toast.success("Survey marked as completed.");
      try {
        await runReportUploads(reportPrereqs, { context: "complete-project" });
      } catch (reportError) {
        console.error(
          "Survey marked as completed, but some reports could not be sent to the backend.",
          reportError
        );
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to mark survey as completed.";
      toast.error(errorMessage);
    } finally {
      setIsStatusActionLoading(false);
    }
  };

  // Remove ASA sprites for a specific segment from scene + local state
  const removeAsaSpritesForSegment = (segmentId, segmentIndex) => {
    const scene = sceneManager.scene;
    if (!scene) return;

    const matches = (obj) => {
      if (!isIrradianceOverlay(obj)) return false;
      const matchesId = segmentId != null && obj.userData?.segmentId === segmentId;
      const matchesIndex =
        Number.isInteger(segmentIndex) && obj.userData?.segmentIndex === segmentIndex;
      return matchesId || matchesIndex;
    };

    const toRemove = [];
    scene.traverse((obj) => {
      if (matches(obj)) toRemove.push(obj);
    });

    if (!toRemove.length) return;

    toRemove.forEach((overlay) => {
      disposeIrradianceOverlay(overlay);
    });

    const removedIds = new Set(toRemove.map((s) => s.uuid));
    setAsaSprites((prev) => prev.filter((s) => !removedIds.has(s.uuid)));
  };

  // Listen for segment geometry changes to invalidate irradiance
  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      if (detail?.affectsAll) {
        let shouldNotify = false;
        for (let i = 0; i < POLYGONS.length; i++) {
          const seg = POLYGONS[i];
          const segmentId = seg?.id ?? null;
          if (!segmentId) continue;

          const result = invalidateIrradianceForSegment(segmentId, {
            clearTofGti: true,
            clearAsa: true,
            markDirty: true,
          });
          shouldNotify = shouldNotify || !!result?.shouldNotify;

          seg.irradiance = {
            ...(seg.irradiance || {}),
            tof: null,
            gti: null,
            asa: null,
            ASA: null,
            TOF: null,
            monthlyASA: null,
            irradiancePoints: null,
            status: { ...(seg.irradiance?.status || {}), tof: "idle", gti: "idle", asa: "idle" },
          };

          removeAsaSpritesForSegment(segmentId, i);
        }

        if (shouldNotify) {
          toast.info("Scene changed, need to recompute irradiance.");
        }
        return;
      }

      const segIndex =
        Number.isInteger(detail.segmentIndex) ? detail.segmentIndex : null;
      const seg =
        segIndex != null ? POLYGONS?.[segIndex] : POLYGONS.find((p) => p?.id === detail.segmentId);

      const segmentId = detail.segmentId ?? seg?.id ?? null;
      if (!segmentId) return;

      const { shouldNotify } = invalidateIrradianceForSegment(segmentId, {
        clearTofGti: true,
        clearAsa: true,
        markDirty: true,
      });

      // Clear segment-level irradiance mirrors so UI doesn't show stale values
      if (seg) {
        seg.irradiance = {
          ...(seg.irradiance || {}),
          tof: null,
          gti: null,
          asa: null,
          ASA: null,
          TOF: null,
          monthlyASA: null,
          irradiancePoints: null,
          status: { ...(seg.irradiance?.status || {}), tof: "idle", gti: "idle", asa: "idle" },
        };
      }

      removeAsaSpritesForSegment(segmentId, segIndex);

      if (shouldNotify) {
        toast.info("Section moved, need to recompute irradiance.");
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("SEGMENT_GEOMETRY_CHANGED", handler);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("SEGMENT_GEOMETRY_CHANGED", handler);
      }
    };
  }, []);

  const handleMagicToggle = () => {
    setMagicToggle((prev) => !prev);
  };

  useEffect(() => {
    if (magicToggle) {
      selectSegment(
        selectedIndices,
        selectedIndices.segmentIndex,
        setSelectedIndices,
        null
      );
      toggleSegmentLabels(true);
    } else {
      toggleSegmentLabels(false);
      if (selectedIndices.segmentIndex != null) {
        showSelectedSegmentLabel(selectedIndices.segmentIndex);
      }
    }
  }, [magicToggle]);

  useEffect(() => {
    if (selectedIndices.segmentIndex !== null && magicToggle) {
      setMagicToggle((prev) => !prev);
    }
  }, [selectedIndices.segmentIndex]);

  const handleRulerClick = () => {
    if (!updateDrawingMode) return;

    if (currentDrawingMode === DrawingModes.RULER) {
      updateDrawingMode(DrawingModes.NONE);
    } else if (currentDrawingMode === DrawingModes.NONE) {
      updateDrawingMode(DrawingModes.RULER);
    }
  };

  const handleSunClick = () => {
    const { scene, camera, controls } = sceneManager.getSceneObjects();
    if (!scene || !camera || !controls) {
      console.error("Sun visualization: scene/camera/controls not ready");
      return;
    }
    initSunSimulation(scene, camera, controls, toolLatitude, toolLongitude);
    setIsSunVisualizationActive(getSunVisualizationVisible());
  };

  // ??? Keep precompute background jobs for TOF/GTI
  // Held until the model is on screen: these jobs pull down a multi-MB GeoTIFF
  // plus a burst of TOF/GTI requests, and running them during the model download
  // steals bandwidth from it on a saturated connection.
  useEffect(() => {
    if (!POLYGONS || POLYGONS.length === 0) return;
    return onModelLoaded(() => {
      ensurePrecomputeForAllSegments(toolLatitude, toolLongitude);
    });
  }, [POLYGONS.length, toolLatitude, toolLongitude]);

  const waitForTofGti = async (segmentIds, { timeoutMs = 60000 } = {}) => {
    if (!segmentIds.length) return { ok: true, errors: [] };
    const start = Date.now();

    while (Date.now() - start < timeoutMs) {
      const errors = [];
      let allDone = true;

      for (const id of segmentIds) {
        const c = getOrCreateCache(id);
        const tofDone = c?.status?.tof === "done";
        const gtiDone = c?.status?.gti === "done";
        const hasError =
          c?.status?.tof === "error" ||
          c?.status?.gti === "error";

        if (hasError) errors.push(id);
        if (!tofDone || !gtiDone) allDone = false;
      }

      if (errors.length) {
        return { ok: false, errors };
      }

      if (allDone) {
        return { ok: true, errors: [] };
      }

      await new Promise((r) => setTimeout(r, 200));
    }

    return { ok: false, errors: [], timeout: true };
  };

  const runIrradianceComputation = async ({
    showOverlayAfterCompute = false,
    followUpLabel = "",
  } = {}) => {
    if (POLYGONS.length === 0) {
      toast.warning("Place a segment first before calculating irradiance.", {
        key: "irradiance-no-segments",
        duration: 5000,
      });
      return false;
    }

    ensurePrecomputeForAllSegments(toolLatitude, toolLongitude);
    const initialMissingSegments = getSegmentsMissingIrradiance(POLYGONS);
    if (initialMissingSegments.length === 0) {
      if (showOverlayAfterCompute) {
        return showIrradianceOverlay();
      }
      return true;
    }

    const totalSegments = initialMissingSegments.length;
    let completedSegments = 0;

    setIrradianceLoading(true);
    updateIrradianceProgressState({
      totalSegments,
      completedSegments,
      followUpLabel,
    });
    await new Promise(requestAnimationFrame);

    try {
      const segmentsWaitingForTofGti = initialMissingSegments.filter((seg) => {
        const c = getOrCreateCache(seg?.id);
        return (
          c?.needsTofGti ||
          c?.status?.tof !== "done" ||
          c?.status?.gti !== "done" ||
          !Array.isArray(c?.gti) ||
          c.gti.length === 0
        );
      });

      if (segmentsWaitingForTofGti.length > 0) {
        segmentsWaitingForTofGti.forEach((seg) => {
          const c = getOrCreateCache(seg?.id);
          enqueueTofGtiIfNeeded(seg, toolLatitude, toolLongitude, {
            force: Boolean(c?.needsTofGti),
          });
        });

        const waitRes = await waitForTofGti(
          segmentsWaitingForTofGti.map((seg) => seg.id),
          { timeoutMs: 60000 }
        );

        if (!waitRes.ok) {
          if (waitRes.timeout) {
            toast.warning("TOF/GTI recalculation timed out. Try again.");
            return false;
          }

          if (waitRes.errors?.length) {
            toast.warning("Some sections failed to recalculate TOF/GTI. Check console.");
          }
          return false;
        }

        segmentsWaitingForTofGti.forEach((seg) => {
          const c = getOrCreateCache(seg.id);
          if (c?.status?.tof === "done" && c?.status?.gti === "done") {
            c.needsTofGti = false;
            IRR_CACHE.set(seg.id, c);
          }
        });
      }

      const yieldToUI = () => new Promise(requestAnimationFrame);

      if (asaSprites.length > 0) {
        asaSprites.forEach((overlay) => {
          disposeIrradianceOverlay(overlay);
        });
        setAsaSprites([]);
      }

      let sunPositions;
      if (
        typeof window !== "undefined" &&
        typeof window.calculateSunPositions === "function"
      ) {
        sunPositions = window.calculateSunPositions(toolLatitude, toolLongitude);
      } else {
        const mod = await import("../ThreeJs/Shading/sunPositioning");
        sunPositions = mod.calculateSunPositions(toolLatitude, toolLongitude);
      }

      sunPositions = (sunPositions || [])
        .filter((s) => s?.position?.z > 0)
        .filter(
          (s, i, arr) =>
            i ===
            arr.findIndex(
              (t) =>
                t.position.x === s.position.x &&
                t.position.y === s.position.y &&
                t.position.z === s.position.z
            )
        );

      const segmentsToCompute = initialMissingSegments.filter((seg) => {
        if (!seg?.id) return false;
        const c = getOrCreateCache(seg.id);

        const ready =
          c?.status?.tof === "done" &&
          c?.status?.gti === "done" &&
          Array.isArray(c?.gti) &&
          c.gti.length > 0;

        return ready && !hasComputedIrradiance(seg);
      });

      if (segmentsToCompute.length === 0) {
        if (getSegmentsMissingIrradiance(POLYGONS).length === 0) {
          if (showOverlayAfterCompute) {
            return showIrradianceOverlay();
          }
          return true;
        }

        toast.info("TOF/GTI are still loading for new sections. Try again in a moment.");
        return false;
      }

      async function gzipJsonPayload(payload) {
        if (typeof CompressionStream === "undefined") return null;

        const encoder = new TextEncoder();
        const inputBytes = encoder.encode(JSON.stringify(payload));

        const cs = new CompressionStream("gzip");
        const writer = cs.writable.getWriter();
        writer.write(inputBytes);
        writer.close();

        return await new Response(cs.readable).arrayBuffer();
      }

      const GPU_RAYCAST_URL = "https://lider.dronifyit.com/gpu-raycast/";
      const REGISTER_URL = `${GPU_RAYCAST_URL}register-scene`;
      const excludeRoots = [];

      for (const seg of POLYGONS) {
        if (seg?.shape) excludeRoots.push(seg.shape);
      }

      const lidarTerrain = sceneManager.scene.getObjectByName("lidarTerrainMesh");
      if (lidarTerrain?.visible) excludeRoots.push(lidarTerrain);

      const lidarArrow = sceneManager.scene.getObjectByName("lidarArrowControl");
      if (lidarArrow?.visible) excludeRoots.push(lidarArrow);

      const {
        buildSceneTrianglesPayload,
        buildRaycastWork,
        buildRaycastResultFromHits,
      } = await import("../helpers/raycastPayloadBuilder");

      const exclusionSpheres = getExclusionSphereConfigs({ enabledOnly: true });
      const scenePayload = buildSceneTrianglesPayload(sceneManager.scene, {
        excludeRoots,
        exclusionSpheres,
      });

      if (!scenePayload || scenePayload.triCount == null) {
        toast.error("Failed to extract scene triangles for registration.");
        return false;
      }

      let sceneId = null;
      try {
        const gzBody = await gzipJsonPayload(scenePayload);
        const headers = { "Content-Type": "application/json" };
        const body = gzBody ? gzBody : JSON.stringify(scenePayload);
        if (gzBody) headers["Content-Encoding"] = "gzip";

        const res = await fetch(REGISTER_URL, {
          method: "POST",
          headers,
          body,
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`${res.status} ${txt}`);
        }

        const json = await res.json();
        sceneId = json?.scene_id;

        if (!sceneId) throw new Error("register-scene missing scene_id");
      } catch (err) {
        console.error("register-scene failed:", err);
        toast.error("Scene registration failed. Check console.");
        return false;
      }

      const works = new Map();
      const jobs = [];
      const skippedSegments = [];

      for (let i = 0; i < segmentsToCompute.length; i++) {
        const seg = segmentsToCompute[i];
        const work = buildRaycastWork(
          sceneManager.scene,
          sunPositions,
          seg.shape,
          seg,
          { sceneId, exclusionSpheres }
        );

        if (!work?.payload) {
          const segmentLabel =
            seg?.label?.element?.innerText ||
            seg?.label?.element?.innerHTML ||
            "Unknown";

          skippedSegments.push({
            segmentLabel,
            segmentId: seg.id,
            reason: work?.emptyReason || "missing-payload",
            sampleSource: work?.sampleSource || "unknown",
            debug: work?.debug || null,
          });

          const cache = getOrCreateCache(seg.id);
          cache.status.asa = "error";
          cache.error =
            work?.emptyReason === "no-sample-points"
              ? "No sample points generated for segment."
              : work?.emptyReason === "no-rays"
                ? "No rays generated for segment."
                : "Failed to build GPU raycast payload.";
          IRR_CACHE.set(seg.id, cache);
          continue;
        }

        works.set(seg.id, work);
        jobs.push({
          segmentId: seg.id,
          payload: work.payload,
        });

        const cache = getOrCreateCache(seg.id);
        cache.status.asa = "pending";
        IRR_CACHE.set(seg.id, cache);

        if (i % 2 === 1) await yieldToUI();
      }

      if (skippedSegments.length > 0) {
        console.warn("Skipped GPU raycast segments:", skippedSegments);
        toast.warning(
          `${skippedSegments.length} section(s) produced 0 ray samples and were skipped. Check console for details.`
        );
      }

      if (jobs.length === 0) {
        toast.error("Failed to build ray payloads. Some sections produced no sample rays.");
        return false;
      }

      const { results: hitsMap, errors } = await runGpuRaycastJobsInPool({
        jobs,
        endpointUrl: GPU_RAYCAST_URL,
        poolSize: 4,
        onJobComplete: (msg) => {
          if (!msg?.ok) return;
          completedSegments += 1;
          updateIrradianceProgressState({
            totalSegments,
            completedSegments,
            followUpLabel,
          });
        },
      });

      if (errors.size > 0) {
        errors.forEach((err, segmentId) => {
          const cache = getOrCreateCache(segmentId);
          cache.status.asa = "error";
          cache.error = err;
          IRR_CACHE.set(segmentId, cache);
        });

        toast.warning(`${errors.size} section(s) failed GPU raycast. Check console for details.`);
        console.error("GPU raycast errors:", Object.fromEntries(errors.entries()));
      }

      let didPersistIrradiance = false;
      for (let i = 0; i < segmentsToCompute.length; i++) {
        const seg = segmentsToCompute[i];
        const segmentId = seg.id;
        const hits = hitsMap.get(segmentId);
        if (!hits) continue;

        const work = works.get(segmentId);
        const rayRes = buildRaycastResultFromHits(work, hits);

        const cache = getOrCreateCache(segmentId);
        const computed = computeASAForSegment(cache.gti, rayRes, seg.id);

        cache.asa = computed.avgASA;
        cache.monthlyASA = computed.monthlyASA;
        cache.irradiancePoints = computed.irradiancePoints;
        cache.displayPoints = computed.displayPoints;
        cache.status.asa = "done";
        cache.error = null;

        IRR_CACHE.set(segmentId, cache);
        markIrradianceClean(segmentId);

        seg.irradiance = {
          ...(seg.irradiance || {}),
          ASA: cache.asa,
          TOF: cache.tof ?? seg.irradiance?.TOF ?? null,
          monthlyASA: cache.monthlyASA,
          irradiancePoints: cache.irradiancePoints,
        };
        didPersistIrradiance = true;

        await yieldToUI();
      }

      const remainingMissingSegments = getSegmentsMissingIrradiance(POLYGONS);
      if (remainingMissingSegments.length > 0) {
        toast.warning(
          `Irradiance could not be completed for ${remainingMissingSegments.length} section${
            remainingMissingSegments.length === 1 ? "" : "s"
          }.`
        );
        return false;
      }

      if (showOverlayAfterCompute) {
        if (!showIrradianceOverlay()) return false;
      }

      if (didPersistIrradiance && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("DESIGN_UNSAVED_CHANGE", {
            detail: { source: "irradiance" },
          })
        );
      }

      return true;
    } catch (err) {
      console.error("Irradiance calculation failed:", err);
      toast.error("Irradiance calculation failed. Check console.");
      return false;
    } finally {
      setIrradianceLoading(false);
      clearIrradianceProgressState();
    }
  };

  const ensureIrradianceReady = async ({
    showOverlayAfterCompute = false,
    followUpLabel = "",
  } = {}) => {
    if (irradianceLoading) return false;

    const missingSegments = getSegmentsMissingIrradiance(POLYGONS);
    if (missingSegments.length === 0) {
      if (showOverlayAfterCompute) {
        return showIrradianceOverlay();
      }
      return true;
    }

    return runIrradianceComputation({
      showOverlayAfterCompute,
      followUpLabel,
    });
  };

  const handleIrradiance = async () => {
    if (POLYGONS.length === 0) {
      setIsIrradianceActive(false);
      toast.warning("Please draw sections to analyze shading first.", {
        key: "irradiance-no-segments",
        duration: 5000,
      });
      return;
    }

    const computedOrToggled = await ensureIrradianceReady({
      showOverlayAfterCompute: true,
    });

    // A failed or incomplete run must never leave the header action looking
    // enabled. The overlay is marked active only by showIrradianceOverlay()
    // after it has data to display.
    if (!computedOrToggled) {
      setIsIrradianceActive(false);
    }
  };

  useEffect(() => {
    const onLidarProgress = (info) => {
      if (info?.visible) {
        const startedAt = Number.isFinite(info.startedAt) ? info.startedAt : Date.now();
        setLidarLoader({
          visible: true,
          title: "Generating LiDAR terrain",
          stageLabel: info.stageLabel || "Processing LiDAR",
          startedAt,
          progress: estimateLidarProgress(startedAt),
        });
      } else {
        setLidarLoader(HIDDEN_LIDAR_LOADER);
      }
    };

    const fetchLidarData = async (lat, lng) => {
      try {
        setLidarTitle("Loading Lidar...");
        setIsLidarBusy(true);
        const lidarData = await getLidar(lat, lng, { onLidarProgress });

        if (lidarData?.data) {
          setLidarData(lidarData.data);
          // Make Solar raster metadata available to the map-tile control before
          // the optional LiDAR mesh is created.
          window.__GOOGLE_SOLAR_RAW__ = lidarData.data.googleSolarRaw || null;
          window.__GOOGLE_SOLAR_SUMMARY__ = lidarData.data.googleSolar || null;
          window.dispatchEvent(new CustomEvent("GOOGLE_SOLAR_DATA_READY"));
          setLidarTitle("Show Lidar");
        } else {
          setLidarData(null);
          setLidarTitle("Lidar not available");
        }
      } catch (err) {
        setLidarData(null);
        setLidarTitle("Lidar not available");
        console.error("Error fetching lidar data:", err);
        toast.warning("LiDAR isn't available for this location.", {
          key: "lidar-unavailable",
          description: "The 3D terrain overlay can't be shown, but you can keep designing.",
          duration: 5000,
        });
      } finally {
        // The PLY helper clears the loader too, but this covers Solar success,
        // early failures, and unanticipated exits from the request.
        setLidarLoader(HIDDEN_LIDAR_LOADER);
        setIsLidarBusy(false);
      }
    };

    
    return onModelLoaded(() => {
      if (DEV_MOD) {
        fetchLidarData(parseFloat(LAT), parseFloat(LNG));
      } else {
        fetchLidarData(
          parseFloat(currentDesign.latitude),
          parseFloat(currentDesign.longitude)
        );
      }
    });
  }, [currentDesign, setLidarLoader]);

  const handleLidarToggle = async () => {
    if (isLidarBusy) return;

    const lidarTerrains = [];
    const lidarArrows = [];
    sceneManager.scene.traverse((obj) => {
      if (obj?.name === "lidarTerrainMesh") lidarTerrains.push(obj);
      if (obj?.name === "lidarArrowControl") lidarArrows.push(obj);
    });

    if (!lidarData) {
      setLidarTitle("Lidar not available");
      toast.warning("LiDAR isn't available for this location.", {
        key: "lidar-unavailable",
        description: "The 3D terrain overlay can't be shown, but you can keep designing.",
        duration: 5000,
      });
      return;
    }

    if (lidarTerrains.length > 0) {
      const nextVisible = !lidarTerrains.some((mesh) => mesh.visible);
      lidarTerrains.forEach((mesh) => {
        mesh.visible = nextVisible;
      });
      lidarArrows.forEach((arrow) => {
        arrow.visible = nextVisible;
      });
      setLidarTitle(nextVisible ? "Hide Lidar" : "Show Lidar");
      setIsLidarActive(nextVisible);
      return;
    }

    try {
      setIsLidarBusy(true);
      setLidarTitle("Loading Lidar...");
      await new Promise(requestAnimationFrame);
      await buildLidarMesh(lidarData);
      setLidarTitle("Hide Lidar");
      setIsLidarActive(true);
    } finally {
      setIsLidarBusy(false);
    }
  };

  const handleDeleteAllSegments = () => {
    if (!hasSegments) return;
    deleteAllSegments();
    setPolygons([]);
    setAsaSprites([]);
    setSegmentProductions([]);
    setSegmentRequestParams([]);
    latestSegmentProductionsRef.current = [];
    latestSegmentRequestParamsRef.current = [];
    setSelectedIndices({ segmentIndex: null, obstacleIndex: null, panelIndex: null });
    setMagicToggle(false);
    setIsIrradianceActive(false);
    toast.success("All sections deleted.");
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onRequest = () => {
      handleDeleteAllSegments();
    };
    window.addEventListener("REQUEST_DELETE_ALL_SEGMENTS", onRequest);
    return () => window.removeEventListener("REQUEST_DELETE_ALL_SEGMENTS", onRequest);
  }, [handleDeleteAllSegments]);

  // ✅ header stats (POLYGONS is a mutable global, so we cannot memoize with [] deps)
  // We keep a small polling tick to reflect updates when panels are added/removed.
  useEffect(() => {
    const id = setInterval(() => setPolygonsTick((t) => t + 1), 400);
    return () => clearInterval(id);
  }, []);

  const totalPanels = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const _ = polygonsTick;
    return POLYGONS.reduce((sum, seg) => sum + (seg.panels?.length || 0), 0);
  }, [polygonsTick]);

  const hasSegments = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const _ = polygonsTick;
    return POLYGONS.length > 0;
  }, [polygonsTick]);

  const panelPmaxKw = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const _ = polygonsTick;
    const samplePanel = POLYGONS.flatMap((seg) => seg.panels || [])[0];
    if (!samplePanel) return 0;
    const matched = panelList.find((p) => p.id === samplePanel.id);
    return matched ? parseInt(matched.pmax, 10) / 1000 : 0;
  }, [panelList, polygonsTick]);

  const dcSystemSizeKw = totalPanels * panelPmaxKw;

  const totalProductionKwh = useMemo(
    () =>
      segmentProductions.reduce(
        (sum, seg) => sum + (seg?.production?.outputs?.ac_annual ?? 0),
        0
      ),
    [segmentProductions]
  );

  const totalProductionDisplayKwh = totalProductionKwh;

  const monthlyProduction = useMemo(
    () =>
      segmentProductions.length > 0
        ? MONTHS.map((_, i) =>
            segmentProductions.reduce(
              (sum, seg) => sum + (seg?.production?.outputs?.ac_monthly?.[i] || 0),
              0
            )
          )
        : [],
    [segmentProductions]
  );

  const chartData = useMemo(
    () => ({
      labels: MONTHS,
      datasets: [
        {
          label: "Monthly Production (kWh)",
          backgroundColor: "#DBDA53",
          borderRadius: 2,
          borderSkipped: "bottom",
          barPercentage: 0.72,
          categoryPercentage: 0.86,
          data: monthlyProduction,
        },
      ],
    }),
    [monthlyProduction]
  );

  const firstSegment = useMemo(() => POLYGONS.find((s) => s.panels?.length > 0), [polygonsTick]);
  const arrayTilt = firstSegment?.details?.tilt ?? 20;
  const arrayAzimuth = firstSegment?.details?.azimuth ?? 180;

  const segmentsWithPanels = useMemo(
    () => POLYGONS.filter((seg) => seg.panels?.length > 0),
    [polygonsTick]
  );

  const segmentMonthlyIrradianceLoss = useMemo(
    () =>
      segmentsWithPanels.length > 0
        ? segmentsWithPanels.map((seg) => {
            const monthlyASA = seg.irradiance?.monthlyASA;
            if (!monthlyASA || !Array.isArray(monthlyASA)) return [];
            return MONTHS.map((_, monthIdx) => {
              const m = monthlyASA[monthIdx];
              const asaVal = m?.asa;
              return typeof asaVal === "number" && !Number.isNaN(asaVal) ? (1 - asaVal) * 100 : null;
            });
          })
        : [],
    [segmentsWithPanels]
  );

  const monthlyIrradianceLoss = useMemo(
    () =>
      segmentsWithPanels.length > 0
        ? MONTHS.map((_, monthIdx) => {
            let weightedSum = 0;
            let capacitySum = 0;

            segmentsWithPanels.forEach((seg) => {
              const monthlyASA = seg.irradiance?.monthlyASA;
              if (!monthlyASA || !monthlyASA[monthIdx]) return;

              const asaVal = monthlyASA[monthIdx].asa;
              const panelCount = seg.panels?.length || 0;

              if (
                typeof asaVal === "number" &&
                !Number.isNaN(asaVal) &&
                panelCount > 0 &&
                panelPmaxKw > 0
              ) {
                const capKw = panelCount * panelPmaxKw;
                const lossPct = (1 - asaVal) * 100;
                weightedSum += lossPct * capKw;
                capacitySum += capKw;
              }
            });

            return capacitySum > 0 ? weightedSum / capacitySum : null;
          })
        : [],
    [segmentsWithPanels, panelPmaxKw]
  );

  const panelSegmentsMissingIrradiance = useMemo(
    () => segmentsWithPanels.filter((seg) => !hasComputedIrradiance(seg)),
    [segmentsWithPanels]
  );

  const isProductionReportReady =
    totalPanels > 0 && panelSegmentsMissingIrradiance.length === 0;

  // console.log("Current PRoject", currentProject)
  const addressText = currentProject?.address || ''


  const runReportAction = async (action) => {
    if (typeof action !== "function" || reportLoading) return;
    setReportLoading(true);
    try {
      await Promise.resolve(action());
    } catch (err) {
      notifyApiError(err, {
        fallback: "Failed to generate the report. Please try again.",
        key: "report-generate",
      });
    } finally {
      setReportLoading(false);
    }
  };

  const buildProductionReportPayload = (overrides = {}) => ({
    ...(() => {
      const productionRows =
        latestSegmentProductionsRef.current.length > 0
          ? latestSegmentProductionsRef.current
          : segmentProductions;
      const requestParamsSource =
        latestSegmentRequestParamsRef.current.length > 0
          ? latestSegmentRequestParamsRef.current
          : segmentRequestParams;
      const req0 = requestParamsSource[0] || {};
      const toNum = (v) =>
        typeof v === "number" ? v : typeof v === "string" ? Number(v) : null;
      const computedMonthlyProduction =
        productionRows.length > 0
          ? MONTHS.map((_, i) =>
              productionRows.reduce(
                (sum, seg) => sum + (seg?.production?.outputs?.ac_monthly?.[i] || 0),
                0
              )
            )
          : [];
      const computedSolarRadiationMonthly =
        productionRows.length > 0
          ? MONTHS.map((_, idx) => {
              let total = 0;
              let count = 0;
              productionRows.forEach((seg) => {
                const arr = seg?.production?.outputs?.solrad_monthly || [];
                if (arr[idx] != null) {
                  total += arr[idx];
                  count += 1;
                }
              });
              return count ? total / count : null;
            })
          : [];
      const computedTotalProduction = productionRows.reduce(
        (sum, seg) => sum + (seg?.production?.outputs?.ac_annual ?? 0),
        0
      );
      const computedDcCapacityFactor =
        dcSystemSizeKw > 0 ? (computedTotalProduction / (dcSystemSizeKw * 8760)) * 100 : null;

      return {
        MONTHS,
        monthlyProduction: computedMonthlyProduction,
        solarRadiationMonthly: computedSolarRadiationMonthly,
        totalProduction: computedTotalProduction,
        totalLoss,
        currentDesign,
        latitude,
        longitude,
        dcSystemSizeKw,
        totalPanels,
        arrayTilt,
        arrayAzimuth,
        moduleType: toNum(req0.module_type),
        arrayType: toNum(req0.array_type),
        dcToAcRatio: toNum(req0.dc_ac_ratio),
        inverterEfficiency: toNum(req0.inv_eff),
        groundCoverageRatio: toNum(req0.gcr),
        albedo: toNum(req0.albedo),
        bifacial: toNum(req0.bifaciality ?? req0.bifacial),
        monthlyIrradianceLoss,
        dcCapacityFactor: computedDcCapacityFactor,
        lossBreakdown: {
          moduleNameplateRating: values.moduleNameplateRating,
          mismatch: values.mismatch,
          connections: values.connections,
          lightInducedDegradation: values.lightInducedDegradation,
          wiring: values.wiring,
          soiling: values.soiling,
          availability: values.availability,
          age: values.age,
          snow: values.snow,
          other: values.other,
        },
        segmentProductions: productionRows,
        segmentRequestParams: requestParamsSource,
        segmentMonthlyIrradianceLoss,
        auth: {
          token: token || "",
        },
        project: {
          id:
            designID ||
            currentDesign?.uuid ||
            currentDesign?.id ||
            currentDesign?.project_id ||
            "",
        },
      };
    })(),
    ...overrides,
  });

  const generateProductionReport = (options = {}) =>
    generatePvwattsReport(buildProductionReportPayload(options));

  // Readiness probe shared by "Save" and "Mark complete" -- both push the same
  // four artifacts and both must skip the ones whose inputs don't exist yet.
  // The production probe is the expensive one (it re-runs the PVWatts
  // simulation), so it only runs once panels are actually placed.
  const collectReportPrereqs = async () => {
    const prereqs = {
      hasSegments,
      hasPanels: totalPanels > 0,
      shadeReady: false,
      productionReady: false,
    };

    if (prereqs.hasSegments) {
      prereqs.shadeReady = getSegmentsMissingIrradiance(POLYGONS).length === 0;
    }

    if (prereqs.hasPanels) {
      prereqs.productionReady = panelSegmentsMissingIrradiance.length === 0;
      if (prereqs.productionReady) {
        const latestProduction = await simulateSystem();
        prereqs.productionReady =
          Array.isArray(latestProduction) && latestProduction.length > 0;
      }
    }

    return prereqs;
  };

  // Regenerates every artifact whose prerequisites are met and pushes it to the
  // backend. Each task passes shouldDownload:false, so this only uploads -- the
  // user never gets a surprise file download from a save.
  const runReportUploads = async (prereqs = {}, { context = "reports" } = {}) => {
    const safePrereqs = {
      hasSegments: !!prereqs.hasSegments,
      hasPanels: !!prereqs.hasPanels,
      shadeReady: !!prereqs.shadeReady,
      productionReady: !!prereqs.productionReady,
    };
    const tasks = [
      {
        label: "shade report",
        canRun: safePrereqs.hasSegments && safePrereqs.shadeReady,
        skipReason: !safePrereqs.hasSegments
          ? "no segments"
          : "irradiance not ready",
        run: () => onDownloadShadeReport?.({ shouldDownload: false }),
      },
      {
        label: "production report",
        canRun: safePrereqs.hasPanels && safePrereqs.productionReady,
        skipReason: !safePrereqs.hasPanels
          ? "no panels placed"
          : "irradiance or production data not ready",
        run: () => generateProductionReport({ shouldDownload: false }),
      },
      {
        label: "facet report",
        canRun: safePrereqs.hasSegments,
        skipReason: "no segments",
        run: () => onDownloadFacetReport?.({ shouldDownload: false }),
      },
      {
        label: "dxf",
        canRun: safePrereqs.hasSegments,
        skipReason: "no segments",
        run: () => onGenerateDxf?.({ shouldDownload: false }),
      },
    ];

    const summary = {
      generated: [],
      skipped: [],
      failed: [],
    };

    setReportLoading(true);
    try {
      for (const task of tasks) {
        if (!task.canRun) {
          console.log(`[${context}] skipped ${task.label}: ${task.skipReason}`);
          summary.skipped.push({
            label: task.label,
            reason: task.skipReason,
          });
          continue;
        }

        try {
          await Promise.resolve(task.run());
          console.log(`[${context}] generated ${task.label}`);
          summary.generated.push(task.label);
        } catch (error) {
          console.error(`Automatic ${task.label} generation failed:`, error);
          summary.failed.push(task.label);
        }
      }
    } finally {
      setReportLoading(false);
    }

    console.log(`[${context}] report summary`, summary);
    if (summary.failed.length > 0) {
      toast.error(
        `${summary.failed.length} report${
          summary.failed.length === 1 ? "" : "s"
        } couldn't be saved to your project.`,
        {
          key: `report-uploads-${context}`,
          description: `Failed: ${summary.failed.join(", ")}. ${
            context === "complete-project"
              ? "The survey was still marked complete."
              : "Your design changes were still saved."
          }`,
          duration: 10000,
        }
      );
    }
    return summary;
  };

  // Save writes the design, then brings every generated artifact back in step
  // with it. Without this the DXF and the reports stay frozen at whatever the
  // last explicit download produced, so a saved design and its stored files
  // could disagree. Reports whose inputs aren't ready are skipped, never failed.
  const handleSaveWithReports = async () => {
    try {
      await Promise.resolve(onSave?.());
    } catch (error) {
      // handleUpdatePolygon already surfaced the failure. Never push artifacts
      // built from a design the backend didn't accept.
      return;
    }

    try {
      const prereqs = await collectReportPrereqs();
      const summary = await runReportUploads(prereqs, { context: "save" });
      if (summary?.generated?.length) {
        toast.success("Project files updated.", {
          key: "report-uploads-save-success",
          description: `Updated: ${summary.generated.join(", ")}.`,
          duration: 6000,
        });
      }
    } catch (error) {
      console.error(
        "Design saved, but its files could not be regenerated:",
        error
      );
    }
  };

  const handleShadeReport = async () => {
    if (reportLoading || irradianceLoading) return;
    if (POLYGONS.length === 0) {
      toast.warning("Please draw sections to generate shade report.", {
        key: "shade-report-no-segments",
        duration: 5000,
      });
      return;
    }
    const ready = await ensureIrradianceReady({
      followUpLabel: "Shade report",
    });
    if (!ready) return;
    return runReportAction(onDownloadShadeReport);
  };
  const handleFacetReport = () => runReportAction(onDownloadFacetReport);
  const handleDxfReport = () => runReportAction(onGenerateDxf);
  const handleProductionReport = async () => {
    if (reportLoading) return;
    if (totalPanels === 0) {
      toast.warning("Please place at least one panel to generate the production report.");
      return;
    }
    if (panelSegmentsMissingIrradiance.length > 0) {
      toast.warning("Please calculate irradiance for panel sections before generating the production report.");
      return;
    }
    if (segmentProductions.length === 0) {
      const latestProduction = await simulateSystem();
      if (!Array.isArray(latestProduction) || latestProduction.length === 0) {
        toast.warning("Production data is not ready yet.");
        return;
      }
    }
    if (reportLoading) return;
    return runReportAction(() => generateProductionReport());
  };

  const iconImgStyle = {
    width: 18,
    height: 18,
    display: "block",
    pointerEvents: "none",
    userSelect: "none",
    WebkitUserDrag: "none",
  };
  const headerSidePadding = 20;
  const textStrong = "var(--ui-text-strong)";
  const textMuted = "var(--ui-text-muted)";
  const logoSlotStyle = {
    width: 50,
    height: '100%',
    display: 'grid',
    placeItems: 'center',
    flex: '0 0 50px',
    transform: `translateX(-${headerSidePadding / 2}px)`,
  };
  const logoImgStyle = {
    width: 30,
    height: 33.52,
    display: 'block',
  };
  const saveGlyphStyle = {
    width: 18,
    height: 18,
    display: "block",
    pointerEvents: "none",
    userSelect: "none",
    WebkitUserDrag: "none",
  };
  const saveGlyphEnabledStyle = {
    ...saveGlyphStyle,
    filter: "brightness(0) saturate(100%)",
  };

  const simulateButtonStyle = {
    height: 40,
    minWidth: 106,
    padding: '0 14px',
    borderRadius: 10,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '1',
    border: '0.6px solid rgba(255, 255, 255, 0.2)',
    cursor: 'pointer',
  };
  const simulateIconStyle = {
    width: 16,
    height: 16,
    fontSize: 16,
    color: "#FFFFFF",
    display: "block",
    flex: "0 0 16px",
  };
  const actionMenuPanelStyle = {
    width: 220,
    minWidth: 220,
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    borderRadius: 15,
    border: "0.6px solid rgba(255, 255, 255, 0.2)",
    background:
      "radial-gradient(76.4% 107.01% at -33.14% 136%, rgba(229, 224, 70, 0.18) 0%, rgba(229, 224, 70, 0) 100%), radial-gradient(46.28% 46.43% at 96.28% 3.57%, rgba(58, 227, 246, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), #0B1E26",
    backdropFilter: "blur(50px)",
    boxShadow: "0 18px 40px rgba(0, 0, 0, 0.32)",
    zIndex: 10020,
  };
  const actionMenuHitAreaStyle = {
    position: "absolute",
    top: "100%",
    right: -20,
    padding: "20px 20px 20px 5px",
    zIndex: 10020,
  };
  const generateSubmenuHitAreaStyle = {
    position: "absolute",
    top: 0,
    right: "100%",
    padding: "20px 0 20px 20px",
    zIndex: 10021,
  };
  const generateSubmenuStyle = {
    ...actionMenuPanelStyle,
    width: 190,
    minWidth: 190,
    marginRight: 8,
  };
  const menuItemStyle = {
    width: "100%",
    minHeight: 35,
    padding: "5px 10px",
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    color: "#FFFFFF",
    fontFamily: '"Reddit Sans", sans-serif',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "20px",
    cursor: "pointer",
    transition: "background 0.18s ease",
  };
  const menuItemContentStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  };
  const generationPillStyle = {
    height: 40,
    minWidth: 343,
    padding: '5px 16px',
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    gap: 2,
    border: '0.6px solid rgba(255, 255, 255, 0.2)',
    overflow: "hidden",
    cursor: "pointer",
  };

  const generationCellStyle = {
    flex: 1,
    padding: "0 18px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center",
    minWidth: 0,
  };

  const generationProductionCellStyle = {
    ...generationCellStyle,
    flex: 1.45,
    minWidth: 108,
  };

  const generationLabelStyle = {
    fontSize: 10,
    lineHeight: "12px",
    color: textMuted,
    fontWeight: 600,
  };

  const generationValueStyle = {
    fontSize: "12px",
    lineHeight: "14px",
    color: textStrong,
    fontWeight: 700,
    letterSpacing: "0.2px",
  };

  const generationDividerStyle = {
    width: 1,
    height: 22,
    background: "rgba(255,255,255,0.14)",
  };

  const generationArrowWrapStyle = {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: textMuted,
  };

  const generationBackdropStyle = {
    position: "fixed",
    top: "var(--app-header-height-scaled)",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    background: "transparent",
    pointerEvents: "none",
    transition: "opacity 220ms ease",
  };

  const generationSidebarStyle = {
    position: "absolute",
    top: 0,
    bottom: 0,
    right: "0",
    width: "min(372px, calc(100vw - var(--right-panel-width-scaled) - 28px))",
    maxWidth: "calc(100vw - var(--right-panel-width-scaled) - 28px)",
    height: "calc(100vh - var(--app-header-height-scaled))",
    overflowY: "auto",
    overflowX: "hidden",
    padding: 14,
    background:
      "radial-gradient(120% 120% at 110% 110%, rgba(48, 72, 54, 0.85) 0%, rgba(11, 30, 38, 0) 60%), linear-gradient(135deg, #1b3b44 0%, #0b1e26 45%, #0b1e26 55%, #243528 100%), radial-gradient(120% 120% at 50% 50%, rgba(0, 0, 0, 0) 35%, rgba(0, 0, 0, 0.35) 100%)",
    boxShadow: "0 22px 46px rgba(0,0,0,0.55)",
    color: textStrong,
    transition: "transform 240ms ease, opacity 180ms ease",
    transformOrigin: "right top",
    zIndex: 10000,
    pointerEvents: "auto",
  };

  const generationSectionCardStyle = {
    marginTop: 12,
    padding: 12,
    borderRadius: 14,
    background: "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
    border: "1px solid rgba(255,255,255,0.14)",
  };

  const generationSectionHeaderStyle = {
    fontSize: 15,
    fontWeight: 700,
    color: textStrong,
    marginBottom: 10,
  };

  const generationFieldGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: 10,
    rowGap: 10,
  };

  const generationFieldLabelStyle = {
    fontSize: 12,
    color: textMuted,
    marginBottom: 6,
    lineHeight: "14px",
  };

  const generationInputStyle = {
    width: "100%",
  };

  const lossEntries = [
    ["moduleNameplateRating", "Module nameplate rating"],
    ["mismatch", "Mismatch"],
    ["connections", "Connections"],
    ["lightInducedDegradation", "Light-induced degradation"],
    ["wiring", "Wiring"],
    ["soiling", "Soiling"],
    ["availability", "Availability"],
    ["age", "Age"],
    ["snow", "Snow"],
    ["other", "Other"],
  ];

  const lossEntryPairs = [];
  for (let i = 0; i < lossEntries.length; i += 2) {
    lossEntryPairs.push(lossEntries.slice(i, i + 2));
  }

  const closeKeyBindingsModal = () => {
    setCapturingBinding(null);
    setKeyBindingsError("");
    setIsKeyBindingsModalOpen(false);
  };

  const saveKeyBindings = () => {
    const savedBindings = saveCameraKeyBindings(draftCameraKeyBindings);
    applyCameraKeyBindings(sceneManager.getSceneObjects()?.controls, savedBindings);
    setCameraKeyBindings(savedBindings);
    closeKeyBindingsModal();
  };

  const resetKeyBindings = () => {
    // Reset the dialog first; the reset is persisted only when the user saves.
    setDraftCameraKeyBindings({ ...DEFAULT_CAMERA_KEY_BINDINGS });
    setCapturingBinding(null);
    setKeyBindingsError("");
  };

  useEffect(() => {
    if (!capturingBinding) return undefined;

    const recordBinding = (binding) => {
      if (!binding) return;
      setDraftCameraKeyBindings((current) => ({ ...current, [capturingBinding]: binding }));
      setKeyBindingsError("");
      setCapturingBinding(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setCapturingBinding(null);
        return;
      }
      event.preventDefault();
      recordBinding(bindingFromAnyKeyEvent(event));
    };

    const handleMouseDown = (event) => {
      if (capturingBinding !== "rotate" && capturingBinding !== "pan") return;
      const binding = bindingFromMouseEvent(event);
      event.preventDefault();
      recordBinding(binding);
    };

    const preventContextMenu = (event) => event.preventDefault();
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("contextmenu", preventContextMenu, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("contextmenu", preventContextMenu, true);
    };
  }, [capturingBinding, draftCameraKeyBindings]);

  const actionMenuItems = [
    {
      key: "generate",
      label: "Generate",
      icon: exportIcon,
      hasSubmenu: true,
      disabled: reportLoading || irradianceLoading,
    },
    {
      key: "ruler",
      label: "Ruler",
      icon: ToggleRulerIcon,
      onClick: handleRulerClick,
    },
    {
      key: "sun",
      label: "Sun Visualization",
      icon: SunVisualizationIcon,
      onClick: handleSunClick,
      active: isSunVisualizationActive,
    },
    {
      key: "measurements",
      label: "Show all measurements",
      icon: ShowAllMeasurementsIcon,
      onClick: handleMagicToggle,
      active: magicToggle,
    },
    {
      key: "key-bindings",
      label: "Key Bindings",
      iconNode: <KeyOutlined style={{ ...ACTION_ICON_STYLE, color: "rgba(255,255,255,0.92)" }} />,
      onClick: () => {
        setDraftCameraKeyBindings(cameraKeyBindings);
        setCapturingBinding(null);
        setKeyBindingsError("");
        setIsKeyBindingsModalOpen(true);
      },
    },
  ];

  const generateMenuItems = [
    {
      key: "shade",
      label: "Shade Report",
      onClick: handleShadeReport,
      disabled: reportLoading || irradianceLoading,
    },
    {
      key: "facet",
      label: "Roof Sections Report",
      onClick: handleFacetReport,
      disabled: reportLoading || irradianceLoading,
    },
    {
      key: "dxf",
      label: "DXF Report",
      onClick: handleDxfReport,
      disabled: reportLoading || irradianceLoading,
    },
    {
      key: "production",
      label: "Production Report",
      onClick: handleProductionReport,
      disabled: !isProductionReportReady || reportLoading,    },
  ];

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const handlePointerDown = (event) => {
      if (!actionMenuRef.current?.contains(event.target)) {
        setIsActionMenuOpen(false);
        setIsGenerateSubmenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const renderActionMenuItem = (item, { submenu = false } = {}) => (
    <button
      key={item.key}
      type="button"
      className="app-header-menu-item"
      onMouseEnter={() => {
        if (item.disabled) return;
        if (item.hasSubmenu) {
          setIsGenerateSubmenuOpen(true);
        } else if (!submenu) {
          setIsGenerateSubmenuOpen(false);
        }
      }}
      onClick={() => {
        if (item.hasSubmenu || item.disabled) return;
        item.onClick?.();
        setIsActionMenuOpen(false);
        setIsGenerateSubmenuOpen(false);
      }}
      disabled={item.disabled}
      aria-disabled={item.disabled}
      style={{
        ...menuItemStyle,
        justifyContent: submenu ? "flex-start" : "space-between",
        ...(item.active
          ? {
              color: "rgba(229, 224, 70, 1)",
            }
          : null),
        ...(item.disabled
          ? {
              opacity: 0.45,
              cursor: "not-allowed",
            }
          : null),
      }}
    >
      <span style={menuItemContentStyle}>
        {item.iconNode || item.icon ? (
          item.iconNode ||
          <img
            src={item.icon}
            alt=""
            aria-hidden="true"
            style={{
              ...ACTION_ICON_STYLE,
              ...(item.active
                ? {
                    filter:
                      "brightness(0) saturate(100%) invert(89%) sepia(38%) saturate(592%) hue-rotate(12deg) brightness(98%) contrast(86%)",
                  }
                : {}),
              ...(item.key === "generate"
                ? { filter: "brightness(0) invert(1)" }
                : {}),
            }}
            draggable={false}
          />
        ) : null}
        <span>{item.label}</span>
      </span>
      {item.hasSubmenu ? <RightOutlined style={{ fontSize: 12, color: "#FFFFFF" }} /> : null}
    </button>
  );

  const getSelectPopupContainer = (node) => node?.parentElement || document.body;

  const generationOverlay =
    typeof document !== "undefined"
      ? createPortal(
          <div
            style={{
              ...generationBackdropStyle,
              opacity: isGenerationOpen ? 1 : 0,
            }}
          >
            <div
              style={{
                ...generationSidebarStyle,
                transform: isGenerationOpen ? "translateX(0)" : "translateX(100%)",
                opacity: isGenerationOpen ? 1 : 0,
              }}
              className="generation-sidebar"
              onClick={(e) => e.stopPropagation()}
            >
              <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
                <Button
                  type="default"
                  icon={
                    <img
                      src={exportIcon}
                      alt="Download"
                      style={{ width: 14, height: 14, display: "block", filter: "brightness(0) invert(1)" }}
                    />
                  }
                  style={{
                    borderRadius: 10,
                    fontWeight: 700,
                    borderColor: "rgba(255,255,255,0.22)",
                    background: "rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.92)",
                  }}
                  disabled={!isProductionReportReady || reportLoading}
                  onClick={handleProductionReport}
                >
                  {reportLoading ? "Generating..." : "Download"}
                </Button>
                <Button
                  type="text"
                  style={{ color: "rgba(255,255,255,0.9)", fontSize: 18, fontWeight: 700 }}
                  onClick={() => setIsGenerationOpen(false)}
                >
                  ×
                </Button>
              </Flex>

              {monthlyProduction.length > 0 && (
                <div
                  className="generation-chart-card"
                  style={{
                    padding: 10,
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                  }}
                >
                  <div style={{ height: 170 }}>
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: {
                          x: {
                            ticks: {
                              color: "rgba(255,255,255,0.65)",
                              font: { size: 11, weight: "500" },
                              maxRotation: 0,
                              minRotation: 0,
                            },
                            grid: { display: false, drawBorder: false },
                            border: { display: false },
                          },
                          y: {
                            ticks: {
                              color: "rgba(255,255,255,0.55)",
                              font: { size: 10, weight: "500" },
                            },
                            grid: { color: "rgba(255,255,255,0.07)", drawBorder: false },
                            border: { display: false },
                            beginAtZero: true,
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={generationSectionCardStyle}>
                <div style={generationSectionHeaderStyle}>System Details</div>
                <div style={generationFieldGridStyle}>
                  <div>
                    <div style={generationFieldLabelStyle}>Module Type</div>
                    <Select
                      size="small"
                      value={systemDetails.module_type}
                      onChange={(val) => handleSystemDetailChange("module_type", val)}
                      style={generationInputStyle}
                      className="segment-obstacle-select generation-control-select"
                      popupClassName="generation-select-dropdown"
                      getPopupContainer={getSelectPopupContainer}
                      options={[
                        { label: "Standard", value: 0 },
                        { label: "Premium", value: 1 },
                        { label: "Thin Film", value: 2 },
                      ]}
                    />
                  </div>
                  <div>
                    <div style={generationFieldLabelStyle}>Dataset</div>
                    <Select
                      size="small"
                      value={systemDetails.dataset}
                      onChange={(val) => handleSystemDetailChange("dataset", val)}
                      style={generationInputStyle}
                      className="segment-obstacle-select generation-control-select"
                      popupClassName="generation-select-dropdown"
                      getPopupContainer={getSelectPopupContainer}
                      options={[
                        { label: "NSRDB", value: "nsrdb" },
                        { label: "TMY2", value: "tmy2" },
                        { label: "TMY3", value: "tmy3" },
                        { label: "International", value: "intl" },
                      ]}
                    />
                  </div>
                  <div>
                    <div style={generationFieldLabelStyle}>DC to AC Ratio</div>
                    <InputNumber
                      size="small"
                      value={systemDetails.dc_ac_ratio}
                      onChange={(val) => handleSystemDetailChange("dc_ac_ratio", val)}
                      min={0.5}
                      max={3}
                      step={0.05}
                      style={generationInputStyle}
                      className="segment-obstacle-input generation-control-input"
                    />
                  </div>
                  <div>
                    <div style={generationFieldLabelStyle}>Inverter Efficiency (%)</div>
                    <InputNumber
                      size="small"
                      value={systemDetails.inv_eff}
                      onChange={(val) => handleSystemDetailChange("inv_eff", val)}
                      min={90}
                      max={99.5}
                      step={0.1}
                      style={generationInputStyle}
                      className="segment-obstacle-input generation-control-input"
                    />
                  </div>
                </div>
              </div>

              <div style={generationSectionCardStyle}>
                <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
                  <div style={generationSectionHeaderStyle}>System Losses</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: textStrong }}>
                    {totalLoss.toFixed(2)}%
                  </div>
                </Flex>
                {lossEntryPairs.map((pair) => (
                  <div key={pair.map(([key]) => key).join("_")} style={generationFieldGridStyle}>
                    {pair.map(([key, label]) => (
                      <div key={key} style={{ marginBottom: 10 }}>
                        <div style={generationFieldLabelStyle}>{label}</div>
                        <InputNumber
                          size="small"
                          value={values[key]}
                          onChange={(val) => handleValueChange(key, val)}
                          min={0}
                          max={100}
                          step={0.1}
                          style={generationInputStyle}
                          className="segment-obstacle-input generation-control-input"
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <Button
                type="primary"
                block
                className="production-update-btn"
                style={{
                  marginTop: 16,
                  borderRadius: 10,
                  fontWeight: 700,
                  height: 40,
                }}
                loading={loading}
                disabled={irradianceLoading}
                onClick={handleSimulateSystem}
              >
                Update Production
              </Button>
            </div>
          </div>,
          document.body
        )
      : null;

  const irradianceLoadingBanner =
    typeof document !== "undefined" && irradianceLoading
      ? createPortal(
          <div
            className={`irradiance-loading-banner ${theme ? "is-dark" : "is-light"}`}
            role="status"
            aria-live="polite"
          >
            <div className="irradiance-loading-banner-row">
              <span className="irradiance-loading-spinner" aria-hidden="true" />
              <span className="irradiance-loading-text">
                {irradianceProgress?.title || "Calculating Irradiance"}
              </span>
            </div>
            {irradianceProgress?.subtitle ? (
              <span className="irradiance-loading-subtext">
                {irradianceProgress.subtitle}
              </span>
            ) : null}
            {irradianceProgress?.progressTotal > 0 ? (
              <div className="irradiance-loading-progress" aria-hidden="true">
                <div
                  className="irradiance-loading-progress-bar"
                  style={{
                    width: `${
                      Math.min(
                        100,
                        ((irradianceProgress.progressCurrent || 0) /
                          irradianceProgress.progressTotal) *
                          100
                      ) || 0
                    }%`,
                  }}
                />
              </div>
            ) : null}
          </div>,
          document.body
      )
    : null;

  const ratingCategories = [
    { key: "cameraTechnique", label: "Camera Technique" },
    { key: "flightPath", label: "Flight Path" },
    { key: "imageQuality", label: "Image Quality" },
  ];

  const ratingModalOverlay =
    typeof document !== "undefined" && isRatingModalOpen
      ? createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(5, 12, 18, 0.62)",
              backdropFilter: "blur(6px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1500,
              padding: 20,
            }}
            onClick={closeRatingModal}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 440,
                borderRadius: 24,
                border: "1px solid rgba(110, 156, 167, 0.35)",
                background:
                  "linear-gradient(180deg, rgba(9, 33, 41, 0.98) 0%, rgba(16, 39, 44, 0.98) 100%)",
                boxShadow: "0 28px 80px rgba(0, 0, 0, 0.45)",
                color: "rgba(255,255,255,0.95)",
                padding: 22,
              }}
            >
              <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
                <Typography.Text
                  style={{ color: "rgba(255,255,255,0.96)", fontSize: 30, fontWeight: 700 }}
                >
                  Rate this survey
                </Typography.Text>
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={closeRatingModal}
                  style={{ color: "rgba(255,255,255,0.82)" }}
                />
              </Flex>

              <div
                style={{
                  height: 1,
                  background: "rgba(158, 190, 195, 0.18)",
                  marginBottom: 18,
                }}
              />

              <div style={{ display: "grid", gap: 14 }}>
                {ratingCategories.map((category) => {
                  const value = surveyRatings[category.key] || 0;

                  return (
                    <div
                      key={category.key}
                      style={{
                        borderRadius: 16,
                        padding: 16,
                        background: "rgba(255,255,255,0.035)",
                        border: "1px solid rgba(158, 190, 195, 0.18)",
                      }}
                    >
                      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                        <Typography.Text
                          style={{ color: "rgba(255,255,255,0.96)", fontSize: 16, fontWeight: 700 }}
                        >
                          {category.label}
                        </Typography.Text>
                        <Typography.Text
                          style={{ color: "rgba(255,255,255,0.92)", fontSize: 16, fontWeight: 700 }}
                        >
                          {value} <span style={{ color: "rgba(255,255,255,0.5)" }}>/ 5</span>
                        </Typography.Text>
                      </Flex>

                      <div style={{ display: "flex", gap: 8 }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() =>
                              setSurveyRatings((prev) => ({
                                ...prev,
                                [category.key]: star,
                              }))
                            }
                            style={{
                              appearance: "none",
                              background: "transparent",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                              lineHeight: 0,
                              color:
                                star <= value
                                  ? "rgba(229, 224, 70, 1)"
                                  : "rgba(122, 148, 153, 0.5)",
                            }}
                            aria-label={`${category.label} ${star} out of 5`}
                          >
                            <StarFilled style={{ fontSize: 22 }} />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div
                  style={{
                    borderRadius: 16,
                    padding: 16,
                    background: "rgba(221, 255, 229, 0.03)",
                    border: "1px solid rgba(158, 190, 195, 0.18)",
                  }}
                >
                  <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                    <Typography.Text
                      style={{ color: "rgba(255,255,255,0.96)", fontSize: 16, fontWeight: 700 }}
                    >
                      Overall
                    </Typography.Text>
                    <Typography.Text
                      style={{ color: "rgba(255,255,255,0.92)", fontSize: 16, fontWeight: 700 }}
                    >
                      {(surveyRatings.cameraTechnique ||
                        surveyRatings.flightPath ||
                        surveyRatings.imageQuality)
                        ? overallSurveyRating.toFixed(1)
                        : "-"}{" "}
                      <span style={{ color: "rgba(255,255,255,0.5)" }}>/ 5</span>
                    </Typography.Text>
                  </Flex>

                  <div
                    style={{
                      width: "100%",
                      height: 8,
                      borderRadius: 999,
                      background: "rgba(255,255,255,0.08)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.max(0, Math.min(100, (overallSurveyRating / 5) * 100))}%`,
                        height: "100%",
                        borderRadius: 999,
                        background: "#E0B63F",
                        transition: "width 160ms ease",
                      }}
                    />
                  </div>
                </div>
              </div>

              <Flex gap={12} style={{ marginTop: 18 }}>
                <Button
                  block
                  onClick={closeRatingModal}
                  style={{
                    height: 42,
                    borderRadius: 12,
                    borderColor: "rgba(158, 190, 195, 0.22)",
                    background: "rgba(255,255,255,0.02)",
                    color: "rgba(255,255,255,0.92)",
                    fontWeight: 700,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  block
                  loading={isStatusActionLoading}
                  disabled={!hasAllSurveyRatings}
                  onClick={handleSubmitSurveyRating}
                  style={{
                    height: 42,
                    borderRadius: 12,
                    background: hasAllSurveyRatings
                      ? "linear-gradient(180deg, #DAD84F 0%, #C9C741 100%)"
                      : "rgba(255,255,255,0.08)",
                    borderColor: hasAllSurveyRatings ? "#DAD84F" : "rgba(255,255,255,0.08)",
                    color: hasAllSurveyRatings ? "#0D1117" : "rgba(255,255,255,0.38)",
                    fontWeight: 700,
                    boxShadow: "none",
                  }}
                >
                  Submit Rating
                </Button>
              </Flex>
            </div>
          </div>,
          document.body
        )
      : null;

  const keyBindingsModal =
    typeof document !== "undefined" && isKeyBindingsModalOpen
      ? createPortal(
          <div
            style={{
              position: "fixed", inset: 0, zIndex: 1600, padding: 20,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "rgba(5, 12, 18, 0.62)", backdropFilter: "blur(6px)",
            }}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeKeyBindingsModal();
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="key-bindings-title"
              style={{
                width: "100%", maxWidth: 500, borderRadius: 20, padding: 22,
                color: "rgba(255,255,255,0.95)",
                border: "1px solid rgba(110, 156, 167, 0.35)",
                background: "linear-gradient(180deg, rgba(9, 33, 41, 0.98) 0%, rgba(16, 39, 44, 0.98) 100%)",
                boxShadow: "0 28px 80px rgba(0, 0, 0, 0.45)",
              }}
            >
              <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
                <Typography.Text id="key-bindings-title" style={{ color: "#fff", fontSize: 22, fontWeight: 700 }}>
                  Key Bindings
                </Typography.Text>
                <Button type="text" icon={<CloseOutlined />} onClick={closeKeyBindingsModal} style={{ color: "rgba(255,255,255,0.82)" }} />
              </Flex>
              <Typography.Text style={{ display: "block", color: "rgba(255,255,255,0.62)", marginBottom: 18 }}>
                Select a binding, then press any keyboard key or mouse button. Keyboard rotate/pan bindings are used while held with a primary-button drag.
              </Typography.Text>

              {[
                ["rotate", "Rotate camera", "Mouse button or keyboard key"],
                ["pan", "Pan camera", "Mouse button or keyboard key"],
                ["panModifier", "Pan camera (hold key)", "Keyboard key"],
                ["zoomIn", "Zoom in", "Keyboard key"],
                ["zoomOut", "Zoom out", "Keyboard key"],
              ].map(([key, label, inputType]) => {
                const isCapturing = capturingBinding === key;
                const hasConflict = conflictingBindingKeys.has(key);
                return (
                  <div key={key} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center", padding: "12px 0", borderTop: "1px solid rgba(158, 190, 195, 0.16)" }}>
                    <div>
                      <Typography.Text style={{ display: "block", color: hasConflict ? "#ff9d92" : "#fff", fontWeight: 700 }}>{label}</Typography.Text>
                      <Typography.Text style={{ color: hasConflict ? "#ff9d92" : "rgba(255,255,255,0.52)", fontSize: 12 }}>{hasConflict ? "Conflicts with another binding" : inputType}</Typography.Text>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setCapturingBinding(key); setKeyBindingsError(""); }}
                      style={{ minWidth: 168, minHeight: 38, padding: "7px 12px", borderRadius: 9, cursor: "pointer", color: hasConflict ? "#ff9d92" : "#fff", fontWeight: 600, border: hasConflict ? "1px solid #ff9d92" : isCapturing ? "1px solid #DAD84F" : "1px solid rgba(158, 190, 195, 0.3)", background: hasConflict ? "rgba(255, 117, 105, 0.1)" : isCapturing ? "rgba(218, 216, 79, 0.15)" : "rgba(255,255,255,0.05)" }}
                    >
                      {isCapturing ? "Press a binding..." : formatCameraBinding(draftCameraKeyBindings[key])}
                    </button>
                  </div>
                );
              })}

              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center", padding: "12px 0", borderTop: "1px solid rgba(158, 190, 195, 0.16)" }}>
                <div>
                  <Typography.Text style={{ display: "block", color: "#fff", fontWeight: 700 }}>Zoom</Typography.Text>
                  <Typography.Text style={{ color: "rgba(255,255,255,0.52)", fontSize: 12 }}>Always available for mouse wheels and trackpads</Typography.Text>
                </div>
                <span style={{ minWidth: 168, textAlign: "center", color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>{formatCameraBinding(draftCameraKeyBindings.zoom)}</span>
              </div>

              {conflictingBindingKeys.size > 0 ? <Typography.Text style={{ display: "block", color: "#ff9d92", marginTop: 4 }}>Each action needs a unique binding before it can be saved.</Typography.Text> : keyBindingsError ? <Typography.Text style={{ display: "block", color: "#ff9d92", marginTop: 4 }}>{keyBindingsError}</Typography.Text> : null}
              <Flex justify="space-between" align="center" gap={12} style={{ marginTop: 18 }}>
                <Button onClick={resetKeyBindings}>Reset to default</Button>
                <Flex gap={10}>
                  <Button onClick={closeKeyBindingsModal}>Cancel</Button>
                  <Button type="primary" onClick={saveKeyBindings} disabled={conflictingBindingKeys.size > 0}>Save</Button>
                </Flex>
              </Flex>
            </div>
          </div>,
          document.body
        )
      : null;

  // Keep report generation silent. `reportLoading` still guards duplicate actions.
  const reportLoadingBanner = null;

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.isIrradianceBusy = irradianceLoading === true;
    window.dispatchEvent(
      new CustomEvent("IRRADIANCE_BUSY_CHANGED", {
        detail: { busy: irradianceLoading === true },
      })
    );
    return () => {
      window.isIrradianceBusy = false;
      window.dispatchEvent(
        new CustomEvent("IRRADIANCE_BUSY_CHANGED", {
          detail: { busy: false },
        })
      );
    };
  }, [irradianceLoading]);

  useEffect(() => {
    return () => {
      setIrradianceProgress(null);
    };
  }, []);

  useEffect(() => {
    setIsIrradianceActive(getIrradianceOverlayVisible());
    setIsLidarActive(getLidarVisible());
  }, [asaSprites, lidarData]);

  return (
    <Layout.Header
      style={{
        height: 'var(--app-header-height-scaled)',
        padding: 0,
        background: 'transparent',
        overflow: 'visible',
        position: 'relative',
        zIndex: 20,
      }}
    >
      <div
        className="app-header-scale ui-scale"
        style={{ padding: `0 ${headerSidePadding}px` }}
      >
        <Flex justify="space-between" align="center" style={{ height: '100%' }}>
          <Flex align="center" style={{ flex: 1, minWidth: 0 }}>
            <div style={logoSlotStyle}>
              <img src={Logo} alt="Logo" style={logoImgStyle} />
            </div>
            <Link to="https://app.eagleyedrone.com/projects" style={{ display: 'inline-flex', alignItems: 'center' }}>
              <ButtonCard>
                <img src={backButtonIcon} alt="Back" style={iconImgStyle} />
              </ButtonCard>
            </Link>
            <div
              style={{
                marginLeft: '15px',
                maxWidth: '420px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography.Text
                className={`${!theme ? 'text-black' : 'text-light'} my-0`}
                style={{ fontSize: '14px', fontWeight: 700, lineHeight: '16px' }}
              >
                {currentProject?.name}
              </Typography.Text>
              {addressText && (
                <Typography.Text
                  className="text-gray my-0 app-header-address"
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '14px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {addressText}
                </Typography.Text>
              )}
            </div>
            {canMarkIncomplete ? (
              <MenuActionButton
                label="Mark as incomplete"
                onClick={handleMarkIncomplete}
                disabled={isStatusActionLoading}
                style={{
                  width: 168,
                  minWidth: 168,
                  marginLeft: 16,
                  flex: "0 0 168px",
                  whiteSpace: "nowrap",
                }}
              />
            ) : null}
            {canMarkComplete ? (
              <MenuActionButton
                label="Mark as completed"
                onClick={handleOpenMarkCompleteModal}
                disabled={isStatusActionLoading || !hasSegments}
                style={{
                  width: 168,
                  minWidth: 168,
                  marginLeft: 16,
                  flex: "0 0 168px",
                  whiteSpace: "nowrap",
                }}
              />
            ) : null}
          </Flex>

          <Flex align="center" justify="flex-end" style={{ flex: 1 }} gap="middle">
            <div style={generationPillStyle} onClick={() => setIsGenerationOpen((prev) => !prev)}>
                <div style={generationCellStyle}>
                  <div style={generationLabelStyle}>Size</div>
                  <div style={generationValueStyle}>
                    {dcSystemSizeKw ? dcSystemSizeKw.toFixed(1) : "0.0"} kW
                  </div>
                </div>

                <div style={generationDividerStyle} />

                <div style={generationCellStyle}>
                  <div style={generationLabelStyle}>Panels</div>
                  <div style={generationValueStyle}>{totalPanels || 0}</div>
                </div>

                <div style={generationDividerStyle} />

                <div style={generationProductionCellStyle}>
                  <div style={generationLabelStyle}>Production</div>
                  <div style={generationValueStyle}>
                    {loading ? "…" : (totalProductionDisplayKwh || 0).toFixed(0)} kWh
                  </div>
                </div>

                <div style={generationArrowWrapStyle}>
                  <img
                    src={isGenerationOpen ? ChevronUpIcon : ChevronDownIcon}
                    alt={isGenerationOpen ? "Chevron Up" : "Chevron Down"}
                    style={{ width: 18, height: 18, display: "block" }}
                  />
                </div>
            </div>

            <Flex align="center" gap={50}>
              <Button
                type="default"
                className="simulate-system-btn"
                icon={<SyncOutlined style={simulateIconStyle} />}
                disabled={irradianceLoading || loading || reportLoading}
                onClick={handleSimulateSystem}
                style={{
                  ...simulateButtonStyle,
                  cursor:
                    irradianceLoading || loading || reportLoading
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <span style={{ color: "inherit", WebkitTextFillColor: "inherit" }}>
                  Simulate
                </span>
              </Button>
              <Flex align="center" gap={5}>
                <MenuActionButton
                  icon={IrradianceIcon}
                  label="Irradiance"
                  onClick={!irradianceLoading ? handleIrradiance : undefined}
                  disabled={irradianceLoading}
                  active={isIrradianceActive}
                  style={{ width: 109 }}
                />

                <MenuActionButton
                  icon={lidarLoader.visible ? undefined : LidarIcon}
                  iconNode={
                    lidarLoader.visible ? (
                      <span
                        className="lidar-button-loader"
                        role="img"
                        aria-label="Downloading Lidar point cloud"
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <circle className="lidar-button-loader-track" cx="12" cy="12" r="8" />
                          <circle className="lidar-button-loader-arc" cx="12" cy="12" r="8" />
                        </svg>
                      </span>
                    ) : null
                  }
                  label="Lidar"
                  onClick={isLidarUnavailable ? undefined : handleLidarToggle}
                  // Keep the button hoverable during the long pipeline job so
                  // its title remains discoverable; the handler already guards
                  // against a concurrent LiDAR action.
                  disabled={isLidarUnavailable}
                  active={isLidarActive}
                  style={{ width: 88 }}
                  className={lidarLoader.visible ? "lidar-loading-action" : ""}
                  title={
                    lidarLoader.visible
                      ? `Downloading Lidar point cloud (${lidarLoader.progress || 0}%)`
                      : lidarTitle
                  }
                />

                <Popconfirm
                  title="Are you sure you want to save changes?"
                  onConfirm={handleSaveWithReports}
                  okText="Yes"
                  cancelText="No"
                  disabled={saveDisabled}
                  overlayClassName="app-popconfirm"
                  okButtonProps={{ className: "app-popconfirm-ok" }}
                  cancelButtonProps={{ className: "app-popconfirm-cancel" }}
                >
                  <div style={{ display: "inline-flex" }}>
                    <MenuActionButton
                      icon={saveIcon}
                      label="Save"
                      disabled={saveDisabled}
                      style={{ width: 85 }}
                      iconStyle={
                        saveDisabled
                          ? saveGlyphStyle
                          : { ...saveGlyphEnabledStyle, filter: "brightness(0) invert(1)" }
                      }
                    />
                  </div>
                </Popconfirm>

                <div
                  ref={actionMenuRef}
                  style={{
                    position: "relative",
                    display: "inline-flex",
                  }}
                  onMouseEnter={() => {
                    setIsGenerationOpen(false);
                    setIsActionMenuOpen(true);
                  }}
                  onMouseLeave={() => {
                    setIsActionMenuOpen(false);
                    setIsGenerateSubmenuOpen(false);
                  }}
                >
                  <ButtonCard
                    onClick={() => {
                      setIsGenerationOpen(false);
                      setIsActionMenuOpen((prev) => !prev);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      width: 40,
                      height: 40,
                    }}
                  >
                    <img
                      src={dropdownDotsIcon}
                      alt="More actions"
                      style={{ ...iconImgStyle, width: 40, height: 40 }}
                      draggable={false}
                    />
                  </ButtonCard>

                  {isActionMenuOpen ? (
                    <div style={actionMenuHitAreaStyle}>
                      <div style={actionMenuPanelStyle}>
                        {actionMenuItems.map((item) => renderActionMenuItem(item))}
                      </div>
                      {isGenerateSubmenuOpen ? (
                        <div style={generateSubmenuHitAreaStyle}>
                          <div style={generateSubmenuStyle}>
                            {generateMenuItems.map((item) => renderActionMenuItem(item, { submenu: true }))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </div>
      {generationOverlay}
      {ratingModalOverlay}
      {keyBindingsModal}
      {irradianceLoadingBanner}
      {reportLoadingBanner}
    </Layout.Header>
  )
}

export default AppHeader
